import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import PaperDraggable from '../../../../../components/PaperDraggable';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { TOpenModalSimulacoes } from '../ListarSimulacoes/Listagem';
import { useMutation, useQueryClient } from 'react-query';
import { useMemo } from 'react';
import { IDataFluxoCaixaSimulacao } from '../../../../../types/api/financeiro/fluxo-caixa-simulacoes';
import { enqueueSnackbar } from 'notistack';
import axiosClient from '../../../../../lib/axios';
import BtnLoading from '../../../../../components/BasicComponents/Buttons/BtnLoading';
import { Grid } from '@mui/system';
import DateComIntervalo from '../../../../../components/DateComIntervalo';
import moment from 'moment';

export default function DialogAddEditSimulacao({
    open,
    defaultValue,
    handleClose
}: DialogAddEditSimulacaoProps) {

    const queryClient = useQueryClient();

    //Valores iniciais
    const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])
    //

    const methods = useForm<IDataFluxoCaixaSimulacao>({
        defaultValues: {
            id: valoresIniciais?.id,
            codigo: valoresIniciais?.codigo,
            descricao: valoresIniciais?.descricao,
            status: valoresIniciais?.status,
            perido_analise_inicio: valoresIniciais?.perido_analise_inicio,
            perido_analise_fim: valoresIniciais?.perido_analise_fim,
            criado_em: valoresIniciais?.criado_em,
            atualizado_em: valoresIniciais?.atualizado_em,
            criado_por: valoresIniciais?.criado_por,
            atualizado_por: valoresIniciais?.atualizado_por,
        },
        mode: 'onChange'
    })

    const { handleSubmit, control, setValue, watch, trigger } = methods;

    //Querys e mutations
    const { mutate: gerarSimulacao, isLoading: isLoadingGerarSimulacao } = useMutation({
        mutationKey: 'gerar-simulacao',
        mutationFn: (inputs: any) => axiosClient.post("/financeiro/fluxo-caixa/gerar-simulacao", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar simulação!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-simulacoes-fluxo-caixa"])
            handleClose()
            enqueueSnackbar('Cadastro de simulação realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarSimulacao, isLoading: isLoadingAttSimulacao } = useMutation({
        mutationKey: 'atualizar-simulacao',
        mutationFn: (inputs: any) => axiosClient.put(`/financeiro/fluxo-caixa/atualizar-simulacao/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar simulação!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-simulacoes-fluxo-caixa"])
            handleClose()
            enqueueSnackbar('Atualização de simulação com sucesso!', { variant: 'success' });
        }
    })
    //

    const onSubmit = (data: IDataFluxoCaixaSimulacao) => {
        const params = {
            status: data.status,
            descricao: data.descricao,
            perido_analise_inicio: data.perido_analise_inicio,
            perido_analise_fim: data.perido_analise_fim,
        }
        //Criar params para enviar para a API, caso seja necessário formatar algum campo
        if (isEdit) {
            atualizarSimulacao(params)
        }
        if (!isEdit) {
            gerarSimulacao(params)
        }
    }

    const opcoesStatusRadio = useMemo(() => [
        { value: "SIMULADO", label: "Simulado" },
        { value: "ATIVO", label: "Ativo" },
        ...(isEdit ? [{ value: "ENCERRADO", label: "Encerrado" }] : [])
    ], [isEdit]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={'md'}
            fullWidth
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">{`${!isEdit ? "Adicionar" : "Editar"} simulação`}</DialogTitle>

            <FormProvider {...methods}>
                <>
                    <DialogContent>
                        <Grid container size={{ xs: 12 }} mt={0.8} spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="perido_analise_inicio"
                                    control={control}
                                    rules={{
                                        required: 'O período de análise é obrigatório',
                                        validate: {
                                            hasEndDate: () => {
                                                const fim = watch("perido_analise_fim");
                                                return fim ? true : 'Ambas as datas são obrigatórias';
                                            }
                                        }
                                    }}
                                    render={({ field, fieldState: { error, invalid } }) => (
                                        <DateComIntervalo
                                            icon={false}
                                            data={{
                                                inicial: field.value ? moment(field.value) : null,
                                                final: watch("perido_analise_fim") ? moment(watch("perido_analise_fim")) : null,
                                            }}
                                            onChangeData={(data) => {
                                                setValue("perido_analise_inicio", data.inicial ? data.inicial.format("YYYY-MM-DD") : null, { shouldValidate: true })
                                                setValue("perido_analise_fim", data.final ? data.final.format("YYYY-MM-DD") : null, { shouldValidate: true })
                                                // Força a revalidação de ambos os campos
                                                trigger(["perido_analise_inicio", "perido_analise_fim"])
                                            }}
                                            datePickerView={['year', 'month', 'day']}
                                            formatString="DD/MM/YYYY"
                                            labelInput="Período de análise *"
                                            error={invalid}
                                            helperText={error ? error.message : ''}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}></Grid>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field, fieldState: { error, invalid } }) => {
                                        return (
                                            <FormControl fullWidth error={invalid} required>
                                                <InputLabel id="nacionalidade-select">Status</InputLabel>
                                                <Select
                                                    labelId="nacionalidade-select"
                                                    label="Status"
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                >
                                                    {opcoesStatusRadio.map((opcao, index) => (
                                                        <MenuItem key={index} value={opcao.value}>{opcao.label}</MenuItem>
                                                    ))}
                                                </Select>
                                                {error && <FormHelperText>{error.message}</FormHelperText>}
                                            </FormControl>
                                        );
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Controller
                                    name="descricao"
                                    rules={{ required: 'O campo é obrigatório' }}
                                    control={control}
                                    render={({ field, fieldState: { error, invalid } }) => {
                                        return (
                                            <TextField
                                                {...field}
                                                multiline
                                                rows={3}
                                                label="Descrição"
                                                fullWidth
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                error={invalid}
                                                helperText={error ? error.message : ""}
                                            />
                                        );
                                    }}
                                />
                            </Grid>

                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button
                            variant="text"
                            onClick={handleClose}
                        >
                            Fechar
                        </Button>
                        <BtnLoading
                            variant="contained"
                            onClick={handleSubmit(onSubmit)}
                            loading={isLoadingAttSimulacao || isLoadingGerarSimulacao}
                            disabled={isLoadingAttSimulacao || isLoadingGerarSimulacao}
                        >
                            {!isEdit ? "Adicionar" : "Atualizar"}
                        </BtnLoading>
                    </DialogActions>
                </>
            </FormProvider>
        </Dialog>
    )
}

interface DialogAddEditSimulacaoProps {
    open: boolean;
    defaultValue: TOpenModalSimulacoes;
    handleClose: () => void;
}
